import { Lang, Api } from "@axl-dev/school-sdk";

const LocalizationStorageKey = "localization";

type LocalizationWords = Record<string, string>;

type LocalizationCache = {
  words: LocalizationWords;
  timestamp: number;
  excludedLangs: Lang[];
  lang: Lang;
};

export class LocalizationStore {
  words: LocalizationWords = {};

  excludedLangs: Lang[] = [];

  get langs(): Lang[] {
    return Object.keys(Lang)
      .filter((x) => !this.excludedLangs.includes(x as Lang))
      .map((x) => x as Lang);
  }

  lang: Lang;

  timestamp?: number;

  constructor(
    private api: Api,
    private onReady: () => void,
  ) {
    this.api = api;
  }

  get cache(): LocalizationCache | null {
    const locCacheJson = localStorage.getItem(LocalizationStorageKey);
    if (locCacheJson != null)
      return JSON.parse(locCacheJson) as LocalizationCache;

    return null;
  }

  set cache(cache: LocalizationCache | null) {
    if (cache == null) {
      localStorage.removeItem(LocalizationStorageKey);

      return;
    }

    localStorage.setItem(LocalizationStorageKey, JSON.stringify(cache));
  }

  /**
   * Temporary code to reset obsolete cache
   * @todo remove after fixing localization cache
   */
  get isCacheObsolete() {
    const cache = this.cache;

    return !cache?.excludedLangs;
  }

  async init(lang: Lang) {
    if (this.isCacheObsolete) this.clear(false);
    else {
      const locCache = this.cache;
      // only if desired lang and cached lang are the same
      if (locCache && locCache?.lang === lang)
        this.setData({
          lang,
          words: locCache.words,
          timestamp: locCache.timestamp,
          needCache: false,
          excludedLangs: locCache.excludedLangs,
        });
      else this.clear(false);
    }

    await this.fetchWords(lang);
  }

  clear(cache = true) {
    this.words = {};
    this.timestamp = undefined;
    this.excludedLangs = [];
    if (cache) this.cache = null;
  }

  private async fetchWords(lang: Lang) {
    const result = await this.api.loc.fetch(lang);
    if (!result.success) return;
    this.setData({
      lang,
      words: result.body.words,
      timestamp: result.body.timestamp,
      needCache: true,
      excludedLangs: result.body.excludedLangs,
    });
    this.onReady();
  }

  private async setData({
    lang,
    words,
    timestamp,
    needCache = true,
    excludedLangs = [],
  }: {
    lang: Lang;
    words: LocalizationWords;
    timestamp: number;
    needCache: boolean;
    excludedLangs: Lang[];
  }) {
    this.lang = lang;
    this.words = words;
    this.timestamp = timestamp;
    this.excludedLangs = excludedLangs;
    if (needCache) this.cache = { timestamp, words, lang, excludedLangs };
  }
}
